import { useCallback, useState } from "react";
import JSZip from "jszip";
import { toUrlFriendly } from "@salestrak/utilities";
import { downloadFile, toastManager } from "@salestrak/ui";
import { useAuth } from "#app/lib/auth-context";
import { resolveMediaObjectUrl } from "#app/lib/media-content";
import { extensionForContentType } from "#app/lib/media-kind";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

// Extension for a downloaded media blob, from the shared MIME→extension
// registry. Videos/PDFs/office docs flow through this path too, so it must
// cover every accepted kind — naming an MP4 ".jpg" produces a corrupt,
// unopenable file. Unknown types get ".bin" rather than a wrong image ext.
function extensionFor(blob: Blob): string {
  return extensionForContentType(blob.type) ?? "bin";
}

// Sanitize a server-stored original filename so it's safe to hand to the
// browser download attribute / a zip entry: drop path separators and control
// chars, then trim. Returns "" if nothing usable remains (→ caller falls back
// to the positional name).
function safeFileName(name: string | null | undefined): string {
  if (!name) return "";
   
  return name.replace(/[/\\]/g, "_").replace(/[\u0000-\u001f]/g, "").trim();
}

/** One downloadable item: its media id and (for non-images) the original
 * uploaded filename. Images are re-encoded on upload and carry no name, so
 * they fall back to the positional `photo-N` form. */
export type DownloadableMedia = Omittable<{
  id: string;
  fileName?: string | null;
}>;

// Concurrent photo-byte fetches when zipping "Download all". Bounds the
// connection fan-out and peak in-flight blob memory.
const DOWNLOAD_CONCURRENCY = 6;

/**
 * Download helpers for permission-gated media (lightbox, media tab).
 * Bytes pull through the same authenticated session cache the previews
 * use (object URLs are fetchable). A media item that carries an original
 * filename (videos, PDFs, office docs) downloads under that name; images
 * (which have no stored name) fall back to a slugified `{prefix}-photo-N.ext`.
 */
export function useMediaDownloads(prefix?: string) {
  const { getAccessToken } = useAuth();
  const [downloadingAll, setDownloadingAll] = useState(false);

  const blobFor = useCallback(
    async (photoId: string): Promise<Blob> => {
      const url = await resolveMediaObjectUrl(photoId, { getAccessToken });
      return (await fetch(url)).blob();
    },
    [getAccessToken],
  );

  const namePrefix = toUrlFriendly(prefix);
  // Prefer the stored original filename; otherwise the positional fallback.
  // The extension always comes from the actual blob type, so an unnamed item
  // never gets a wrong/missing extension.
  const mediaFileName = useCallback(
    (position: number, blob: Blob, originalName?: string | null) => {
      const original = safeFileName(originalName);
      if (original) return original;
      return `${namePrefix ? `${namePrefix}-` : ""}photo-${position}.${extensionFor(blob)}`;
    },
    [namePrefix],
  );

  /** Downloads one media item. `position` is its 1-based slot in the
   * surrounding list (used only for the positional-fallback filename). */
  const downloadPhoto = useCallback(
    async (photoId: string, position: number, fileName?: string | null) => {
      try {
        const blob = await blobFor(photoId);
        downloadFile(blob, mediaFileName(position, blob, fileName));
      } catch {
        toastManager.add({ title: "Failed to download photo", type: "error" });
      }
    },
    [blobFor, mediaFileName],
  );

  /** Zips every item and downloads the archive as
   * `{prefix}-{zipName}.zip` (each part optional, "photos" fallback). */
  const downloadAllPhotos = useCallback(
    async (mediaItems: DownloadableMedia[], zipName?: string) => {
      if (!mediaItems.length || downloadingAll) return;
      setDownloadingAll(true);
      try {
        const zip = new JSZip();
        // Distinct original filenames can collide (two "scan.pdf" attachments,
        // or any item falling back to the same positional name is already
        // unique). Disambiguate so a collision doesn't silently drop a file
        // from the archive. Safe across the worker lanes below: the check +
        // insert runs synchronously after each await, so lanes can't interleave
        // within it.
        const usedNames = new Set<string>();
        const uniqueName = (name: string) => {
          if (!usedNames.has(name)) {
            usedNames.add(name);
            return name;
          }
          const dot = name.lastIndexOf(".");
          const base = dot > 0 ? name.slice(0, dot) : name;
          const ext = dot > 0 ? name.slice(dot) : "";
          let n = 2;
          while (usedNames.has(`${base} (${n})${ext}`)) n++;
          const candidate = `${base} (${n})${ext}`;
          usedNames.add(candidate);
          return candidate;
        };
        // Bounded worker pool: a media tab can hold hundreds of photos, and
        // Promise.all over all of them would fire hundreds of concurrent
        // presigned-GET fetches and decode every full-size blob into memory at
        // once (a tab-OOM / connection storm). Fetch in a fixed number of lanes
        // instead, adding each blob to the zip as it lands.
        let next = 0;
        let failed = 0;
        const worker = async () => {
          while (next < mediaItems.length) {
            const i = next++;
            // Skip a photo that can't be fetched (deleted/revoked server-side, a
            // 404 on its presigned GET) instead of letting one rejection abort
            // the whole archive — the user still gets every photo that resolved.
            try {
              const item = at(mediaItems, i, "media item");
              const blob = await blobFor(item.id);
              zip.file(uniqueName(mediaFileName(i + 1, blob, item.fileName)), blob);
            } catch {
              failed++;
            }
          }
        };
        await Promise.all(
          Array.from({ length: Math.min(DOWNLOAD_CONCURRENCY, mediaItems.length) }, worker),
        );
        if (failed === mediaItems.length) {
          toastManager.add({ title: "Failed to download photos", type: "error" });
          return;
        }
        const archive = await zip.generateAsync({ type: "blob" });
        const zipBase = [namePrefix, toUrlFriendly(zipName)].filter(Boolean).join("-");
        downloadFile(archive, `${zipBase || "photos"}.zip`);
        if (failed > 0) {
          toastManager.add({ title: `${failed} photo${failed === 1 ? "" : "s"} couldn't be downloaded`, type: "error" });
        }
      } catch {
        toastManager.add({ title: "Failed to download photos", type: "error" });
      } finally {
        setDownloadingAll(false);
      }
    },
    [blobFor, mediaFileName, namePrefix, downloadingAll],
  );

  return { downloadPhoto, downloadAllPhotos, downloadingAll };
}
